import { createFilter, type GroupBase } from 'react-select';
import ReactCreatableSelect from 'react-select/creatable';

import { Alert, AlertDescription, Flex } from '@chakra-ui/react';
import classNames from 'classnames';

import { formatString, isDefined } from '@enterprise/core';

import { MAX_NUMBER } from '#constants/number';

import type { CreatableSelectProps } from './types/creatable-select.props';

import GroupHeading from './components/group-heading.component';
import Group from './components/group.component';
import Input from './components/input.component';
import MultiValue from './components/multi-value.component';
import VirtualMenuList from './components/virtual-menu-list.component';
import { useCreatableSelectConfiguration } from './hooks/creatable-select-configuration.hook';

import cssClasses from './select.module.css';

export function CreatableSelect<
  IsMulti extends boolean = false,
  Option = unknown,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  canPaste = false,
  className,
  getNewOptionData,
  maxItems = MAX_NUMBER,
  maxVisible,
  onExceeded,
  onOptionsCreated,
  parseRange,
  validateNewOption,
  value,
  ...props
}: CreatableSelectProps<IsMulti, Option, Group>) {
  const { components, options } = props;
  const { createdOptions, exceeded, handleCreateOptions } = useCreatableSelectConfiguration<IsMulti, Option, Group>({
    getNewOptionData,
    maxItems,
    onExceeded,
    onOptionsCreated,
    parseRange,
    validateNewOption,
    value,
  });

  return (
    <Flex direction="column">
      {isDefined(exceeded) && (
        <Alert
          rounded="lg"
          status="error"
          variant="subtle">
          <AlertDescription>
            {formatString(
              'Trying to add {0} items, {1} is the maximum # of items allowed, please use the upload functionality',
              `${exceeded}`,
              `${maxItems}`
            )}
          </AlertDescription>
        </Alert>
      )}
      <ReactCreatableSelect
        {...{ ...props, getNewOptionData, value }}
        canPaste={canPaste}
        className={classNames(cssClasses.hspSelect, className)}
        classNamePrefix="hsp-select"
        components={{
          Group,
          GroupHeading,
          Input,
          MenuList: VirtualMenuList,
          MultiValue,
          ...components,
        }}
        createOptionPosition="first"
        filterOption={createFilter({ ignoreAccents: false })}
        maxVisible={maxVisible}
        menuPlacement="auto"
        menuShouldScrollIntoView
        onCreateOption={handleCreateOptions}
        onOptionsPasted={handleCreateOptions}
        options={[...createdOptions, ...(options ?? [])]}
      />
    </Flex>
  );
}
